import {
  Center,
  Loader,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { BorrowerDetailsTable, DataSummaryCard, Page } from '@/components';
import { yearsToMonths } from '@/helpers/date';
import { removeNumberFormatting, roundNumber, sliceToDecimals } from '@/helpers/number';
import { initialState } from '@/data/features/proposal/proposal';
import { formatNumberWithCommas } from '@/helpers/string';
import TableSimple from '@/components/TableSimple';
import {
  useCalculateCycleMortgageRepaymentTableMutation,
  useCalculateDisposalboardMutation,
  useGetOfferByIdMutation,
} from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import CurrencyNisIcon from '@/assets/images/currencyNis.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './summary.module.css';
import { buildUnifiedDisposalBoard } from '../Proposal/BorrowersDetails/func';

const Summary = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(null);
  const [proposal, setProposal] = useState(initialState);
  const [proposalOffer, setProposalOffer] = useState(null);
  const suffix = <CurrencyNisIcon style={{ width: '12px' }} />;
  const disposalboard = proposalOffer?.disposalboard ?? [];
  const [disposalboardMutationConfig] = useCalculateDisposalboardMutation();
  const [apiGetOfferById] = useGetOfferByIdMutation();
  const [calculateCycleMortgageRepaymentTable] = useCalculateCycleMortgageRepaymentTableMutation();
  const [error, setError] = useState(false);
  let boxesTitles = {
    boxA: '',
    boxB: '',
  };
  const { id } = useParams();
  const [updatedLoanAmount, setUpdatedLoanAmount] = useState(proposal?.loanAmount || 0);
  const [loanPeriod, setLoanPeriod] = useState(yearsToMonths(proposal?.period || 0));

  const sumOfReturnsOfFirstOption = disposalboard?.[0]?.totalReturn ?? 0;
  const sumOfReturnsOfSecondOption = disposalboard?.[1]
    ? disposalboard?.[1].totalReturn : 0;

  const sumOfReturnsOfInterestFirstOption = disposalboard?.[0]?.totalInterestReturn ?? 0;
  const sumOfReturnsOfInterestSecondOption = disposalboard?.[1] ? disposalboard?.[1].totalInterestReturn : '';

  const details = [
    {
      label: t('pages.summary.label.loanType'),
      value: proposal?.loanTypeNameHebrew,
    },
    {
      label: t('pages.summary.label.borrowerNumber'),
      value: proposal?.borrower?.length,
    },
    {
      label: t('pages.summary.label.loanAmount'),
      value: formatNumberWithCommas(proposal?.loanAmount),
      suffix,
    },
    {
      label: t('pages.summary.label.paymentPeriodInMonths'),
      value: loanPeriod,
    },
  ];

  const planADetails = [
    `${formatNumberWithCommas(proposal?.loanAmount)} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[0]?.disposalBoard?.[0]?.totalMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[0]?.maxMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfInterestFirstOption))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfFirstOption))} ₪`,
    `${sliceToDecimals(Number(sumOfReturnsOfFirstOption) / Number(updatedLoanAmount).toFixed(2), 2)} ₪`,
  ];

  const planBDetails = [
    `${formatNumberWithCommas(proposal?.loanAmount)} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[1]?.disposalBoard?.[0]?.totalMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[1]?.maxMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfInterestSecondOption))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfSecondOption))} ₪`,
    `${sliceToDecimals(disposalboard?.[1]?.returnConversion, 2)} ₪`,
  ];

  const plansTableHead = [
    t('pages.summary.label.loanAmount'),
    t('pages.summary.label.paymentPerMonth'),
    t('pages.summary.label.maxPaymentPerMonth'),
    t('pages.summary.label.sumOfReturnsOfInterest'),
    t('pages.summary.label.totalPaymentInPeriodEnd'),
    t('pages.summary.label.forEvryShekelYouWillPay'),
  ];

  const tableHead = [
    t('pages.summary.table.head.month'),
    t('pages.summary.table.head.remaining'),
    t('pages.summary.table.head.monthlyPaymentWithoutInterest'),
    t('pages.summary.table.head.monthlyPaymentInterest'),
    t('pages.summary.table.head.totalPayment'),
  ];

  const risk0 = disposalboard[0]?.risk ?? -1; // Default to -1 if undefined
  const risk1 = disposalboard[1]?.risk ?? -1; // Default to -1 if undefined

  if (disposalboard.length === 1) {
    // Single-element cases
    boxesTitles = {
      boxATitle:
        risk0 === 0 ? t('pages.offer.box.title.regular')
          : risk0 === 1 ? t('pages.offer.box.title.medium')
            : risk0 === 2 ? t('pages.offer.box.title.high')
              : '',
      boxBTitle:
        risk1 === 0 ? t('pages.offer.box.title.regular')
          : risk1 === 1 ? t('pages.offer.box.title.medium')
            : risk1 === 2 ? t('pages.offer.box.title.high')
              : '',
    };
  } else {
    // Multi-element cases
    boxesTitles = {
      boxATitle:
        risk0 === 0 ? t('pages.offer.box.title.regular')
          : risk0 === 1 ? t('pages.offer.box.title.medium')
            : risk0 === 2 ? t('pages.offer.box.title.high')
              : '',
      boxBTitle:
        risk1 === 0 ? t('pages.offer.box.title.regular')
          : risk1 === 1 ? t('pages.offer.box.title.medium')
            : risk1 === 2 ? t('pages.offer.box.title.high')
              : '',
    };
  }

  const tableBodyA = proposalOffer?.disposalboard?.[0]?.disposalBoard?.map((item) => [
    item?.period,
    `${formatNumberWithCommas(roundNumber(item.fundBalance)) || 0} ₪`,
    `${formatNumberWithCommas(roundNumber(item.monthlyFundReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.monthlyInterestReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.totalMonthlyReturn))} ₪`,
  ]);

  const tableBodyB = proposalOffer?.disposalboard?.[1]?.disposalBoard?.map((item) => [
    item?.period,
    `${formatNumberWithCommas(roundNumber(item.fundBalance)) || 0} ₪`,
    `${formatNumberWithCommas(roundNumber(item.monthlyFundReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.monthlyInterestReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.totalMonthlyReturn))} ₪`,
  ]);

  const planADisposalboardtableData = {
    head: tableHead,
    body: tableBodyA,
  };

  const planBDisposalboardtableData = {
    head: tableHead,
    body: tableBodyB,
  };

  const planADetailsTable = {
    head: plansTableHead,
    body: [planADetails],
  };
  const planBDetailsTable = {
    head: plansTableHead,
    body: [planBDetails],
  };

  useEffect(() => {
    const getOfferById = async (offerId) => {
      const { data: result } = await apiGetOfferById({ id: offerId });
      const offer = result?.data ?? null;
      return offer;
    };
    const calculateSummary = async (offerId) => {
      if (!offerId) {
        setError(true);
      }
      try {
        setLoading(true);
        const offer = await getOfferById(offerId);
        if (!offer) {
          setError(true);
        }

        let cycleRepaymentTable = [];
        const {
          propertyValuation,
          loanAmount,
          income,
          outcome,
          borrower,
          loan,
          period,
          risk,
          _id,
          isRecycling,
          cycleMortgage,
          loanTypeName,
        } = offer;

        const formattedPropertyValuation = removeNumberFormatting(String(propertyValuation));
        const formattedLoanAmount = removeNumberFormatting(String(loanAmount));
        const formattedIncome = removeNumberFormatting(String(income));
        const formattedOutcome = removeNumberFormatting(String(outcome));

        const cycleLoansWithCycleRateType = ['renovationOrExpansion', 'anyPurposeWithPropertyCollateral'];
        const objectToSendForcalculate = {
          propertyValuation: formattedPropertyValuation,
          loan: loan._id,
          loanAmount: formattedLoanAmount,
          income: formattedIncome,
          outcome: formattedOutcome,
          period,
          risk,
          borrower,
          rateType: cycleLoansWithCycleRateType.includes(loan.title) ? 'cycle' : 'regular',
        };

        let updatedLoanAmount = Number(loanAmount);
        let cycleLoans;
        let loansToMerge;
        const { loans } = offer.cycleMortgage || {};

        if (isRecycling) {
          updatedLoanAmount += loans.reduce((sum, l) => sum + Number(l.totalAmount), 0);
          cycleLoans = loans.filter((l) => l.isBetterToChange === false);
          loansToMerge = loans.filter((l) => l.isBetterToChange === true);

          if (loanTypeName === 'anyPurposeWithPropertyCollateral') {
            loansToMerge = loans.filter((l) => l.isBetterToChange === true && l.loanPurpose === 'אחר');
            cycleLoans = loans.filter((l) => l.isBetterToChange === false || l.loanPurpose === 'דיור');
          }

          const { data: repaymentTableData } = await calculateCycleMortgageRepaymentTable({
            updatedLoansForSaveAndCalculation: cycleLoans,
          }).unwrap();

          setLoanPeriod(Math.max(yearsToMonths(period), repaymentTableData?.length || 0));

          cycleRepaymentTable = repaymentTableData;
          // eslint-disable-next-line max-len
          objectToSendForcalculate.loanAmount += loansToMerge.reduce((sum, l) => sum + Number(l.totalAmount), 0);

          if (cycleMortgage.isMortgageRepaymentIncludedInPreviousLiabilities === 'yes') {
            objectToSendForcalculate.outcome -= loansToMerge.reduce(
              (sum, item) => sum + (item.monthlyPayment || 0),
              0,
            );
          } else if (cycleMortgage.isMortgageRepaymentIncludedInPreviousLiabilities === 'no') {
            objectToSendForcalculate.outcome += cycleLoans.reduce(
              (sum, item) => sum + (item.monthlyPayment || 0),
              0,
            );
          }
        }

        setUpdatedLoanAmount(updatedLoanAmount);
        const {
          title,
          hebrew: h,
          _id: loanId,
        } = loan;

        const objToState = {
          propertyValuation,
          loanTypeName: title,
          loanTypeNameHebrew: h,
          loanAmount: updatedLoanAmount,
          income,
          outcome,
          id: _id,
          loan: loanId,
          borrower,
          period,
        };

        setProposal(objToState);

        const {
          data: calculateData,
          isError: calculateErrorResponse,
        } = await getMutationResult(objectToSendForcalculate, disposalboardMutationConfig);

        const { data: calculate } = calculateData;

        const { mixes = [] } = calculate;
        let unifiedMixes = mixes;

        if (cycleRepaymentTable.length > 0 && mixes.length > 0) {
          const unified = buildUnifiedDisposalBoard(
            mixes,
            cycleRepaymentTable,
          );

          unifiedMixes = [unified];
        }

        if (!calculateErrorResponse) {
          setProposalOffer({
            disposalboard: unifiedMixes,
            period: loanPeriod,
            risk,
          });
        }
      } catch (err) {
        console.error('Error:', err);
        notifications.show({
          title: t('שגיאה'),
          message: t('שגיאה בסיכום הצעה'),
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    };
    calculateSummary(id);
  }, [id]);

  if (loading) return <Center><Loader /></Center>;

  return (
    !error ? (
      <>
        <Page
          title={t('pages.summary.title')}
          extraSize="xl"
          hideHelp
          hideNews
        >

          <Page.Head
            title={t('pages.summary.title')}
            subtitle={t('pages.summary.subtitle')}
          />

          <div>
            <DataSummaryCard details={details} />
          </div>

          <div>
            <h3>{t('pages.summary.title.borrowerDetails')}</h3>
            <BorrowerDetailsTable borrowers={proposal.borrower} />
          </div>

          {disposalboard?.length > 0 && (
            <>
              <h3>{boxesTitles.boxATitle}</h3>
              <div className={classes.summaryContainer}>
                <TableSimple data={planADetailsTable} />
                <div className={classes.summaryCollapse}>
                  <Page.Section
                    title={`${t('pages.summary.title.disposalBoardFor')}${boxesTitles.boxATitle}`}
                    withCollapse
                    withBackground
                    startClose>
                    <TableSimple data={planADisposalboardtableData} />
                  </Page.Section>
                </div>
              </div>
            </>
          )}

          {disposalboard?.length > 1 && (
            <>
              <h3>{boxesTitles.boxBTitle}</h3>
              <div className={classes.summaryContainer}>
                <TableSimple data={planBDetailsTable} />
                <div className={classes.summaryCollapse}>
                  <Page.Section
                    title={`${t('pages.summary.title.disposalBoardFor')}${boxesTitles.boxBTitle}`}
                    withCollapse
                    withBackground
                    startClose>
                    <TableSimple data={planBDisposalboardtableData} />
                  </Page.Section>
                </div>
              </div>
            </>
          )}
        </Page>
      </>
    ) : <Center>{t('globals.error.title')}</Center>
  );
};

export default Summary;
